import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Backend, BackendKind } from "./types";

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["backends"] });

export function useBackends() {
  return useQuery({
    queryKey: ["backends"],
    queryFn: async () => {
      const body = await apiFetch<{ backends: Backend[] }>("/api/v1/backends");
      return body.backends;
    },
  });
}

/** Resolve a backend id to a friendly display string. Falls back to the id
 *  itself when the backends list isn't loaded yet or the id is unknown. */
export function useBackendLabel() {
  const backends = useBackends();
  const byId = new Map((backends.data ?? []).map((b) => [b.id, b] as const));
  return (id: string): { label: string; kind?: string } => {
    const b = byId.get(id);
    if (!b) return { label: id };
    return { label: b.name, kind: b.kind };
  };
}

export interface BackendCreate {
  name: string;
  kind: BackendKind;
  credentials: Record<string, string>;
}

export function useCreateBackend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BackendCreate) =>
      apiFetch<Backend>("/api/v1/backends", { method: "POST", body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateBackend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: Backend) =>
      apiFetch<Backend>(`/api/v1/backends/${b.id}`, { method: "PUT", body: b }),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteBackend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/backends/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidate(qc),
  });
}
